/**
 * Owner-confirmable practice facts used across the site copy
 * (services, FAQs, policies). Change values here, not in page text.
 *
 * The booking/cancellation numbers mirror the backend config in public API
 * contract v1 §5 (MIN_BOOKING_NOTICE_HOURS, CANCELLATION_WINDOW_HOURS,
 * LATE_CANCEL_FEE_PERCENT). If the owner changes one, change both.
 */
import config from "../config";

export const PRACTICE = {
  // Terms: services are for adults only.
  minClientAge: 18,

  // D10: video platform for sessions (backend auto-creates a Google Meet
  // link per booking unless SESSION_MEETING_URL overrides it).
  meetingPlatform: "Google Meet",

  // D6: booking and cancellation policy. Late-cancel fee: her November 2026
  // correction, 50% of the session fee (contract v1.3 §5
  // LATE_CANCEL_FEE_PERCENT=50).
  minBookingNoticeHours: 24,
  cancellationWindowHours: 12,
  lateCancelFeePercent: 50,

  // Group programmes: full refund of a paid group fee if the client
  // withdraws at least this long before the first session.
  groupWithdrawalHours: 48,
};

// Effective date of the current Privacy / Terms / Refund / Accessibility copy.
// Bump this (and POLICY_VERSION) whenever the owner approves new wording.
export const POLICY_LAST_UPDATED = "2026-11-02";

/** "2026-09-24" -> "September 24, 2026" (no local-time drift). */
export function formatPolicyDate(isoDate = POLICY_LAST_UPDATED) {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** "the full session fee" at 100%, otherwise "50% of the session fee". */
export function lateCancelFeeText(percent = PRACTICE.lateCancelFeePercent) {
  return percent >= 100 ? "the full session fee" : `${percent}% of the session fee`;
}

/**
 * When and how payment is made (pay at session at launch). Her round-2
 * answer 1.1: by bank transfer or cheque, before or at the session.
 */
export function paymentTimingText(paymentsEnabled = config.features.paymentsEnabled) {
  return paymentsEnabled
    ? "Payment for paid sessions is taken securely online when you book."
    : "No payment is taken when you book online. Pay by bank transfer or cheque before or at your session.";
}

/**
 * Online bookings wait for her approval (round 2, answer 6): the booking
 * holds the slot as a request until she confirms it by email.
 */
export const BOOKING_REQUEST_TEXT = "Online bookings are requests: I confirm each one by email.";

/** Shown once on each page that lists prices (round 2, answer 1.7). */
export const CURRENCY_NOTE = "Prices are in Bahamian dollars (B$1 = US$1).";

/**
 * mailto: link for a service that is registered for by email instead of the
 * online slot picker (bookableOnline false, e.g. when the owner turns a
 * service off online booking in the backend).
 */
export function registerByEmailHref(serviceTitle) {
  return `mailto:${config.business.contactEmail}?subject=${encodeURIComponent(`${serviceTitle} registration`)}`;
}
